/**
 * PATH: packages/email-hub/src/feature/lib/inboxFilterPills.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-INBOXFILTERPILLS-001
 * PURPOSE: Pure builder that projects EVERY active inbox constraint
 *          (category chips, read/star, allocation, jobs, sender,
 *          company, dates, attachments, mailboxes, search operators,
 *          muted categories) into one flat list of removable pills.
 * WHY:     Operator 2026-07-06 — "look how hard it is to search and
 *          quickly filter or get to know what I am filtering". The
 *          old Active Filters row only rendered 7 of 13+ dimensions;
 *          the rest (multi-job, mailbox picks, dates, attachments,
 *          search tokens, mutes) narrowed the list invisibly. One
 *          derived model = one legible answer to "why is my inbox
 *          showing this subset?".
 * FLOW:
 *   ┌──────────────────┐   ┌──────────────────────┐   ┌─────────────┐
 *   │ InboxFilterPill  │ → │ buildInboxFilterPills │ → │ FilterPill[]│
 *   │ State (snapshot) │   │ (pure, ordered)       │   │ id+label    │
 *   └──────────────────┘   └──────────────────────┘   └─────────────┘
 * DEPENDENCIES:
 *   - parseEmailSearchQuery: ParsedOperatorToken shape + operator keys
 *     (token pills mirror what the search bar parsed).
 * RELATED:
 *   - components/InboxFilterPills.tsx: renders the output.
 *   - pages/InboxPage.tsx: owns the state snapshot + removal dispatch.
 */

import {
  tokenizeEmailSearch,
  type EmailSearchOperatorKey,
  type ParsedOperatorToken,
} from './parseEmailSearchQuery'

/** One removable pill in the strip.
 *  ``kind`` splits true query-narrowing filters ('filter' — counted in
 *  the funnel badge, drives the result-count strip) from standing
 *  preferences that also narrow the list but persist across sessions
 *  ('standing' — shown for legibility, never counted as "you have a
 *  filter on"). */
export interface FilterPill {
  /** Stable removal id, e.g. `cat:quote_request`, `token:from`. */
  id: string
  /** Visible pill text. */
  label: string
  /** Hover text — spells out what removing the pill does. */
  title: string
  kind: 'filter' | 'standing'
}

/** Snapshot of every filter dimension InboxPage holds. Pure data in —
 *  no setters, no store handles — so the builder is trivially testable. */
export interface InboxFilterPillState {
  selectedCategories: string[]
  /** Resolves a category key to its display label (CATEGORY_CONFIG). */
  categoryLabel: (key: string) => string
  isReadFilter?: boolean | undefined
  isStarredFilter?: boolean | undefined
  allocationFilter?: 'allocated' | 'unallocated' | undefined
  jobIdFilter?: number | undefined
  jobIdsIn: number[]
  fromAddressContains: string
  companyDomain: string
  receivedAtGte?: string | undefined
  receivedAtLte?: string | undefined
  hasAttachments?: boolean | undefined
  hasCalendarEvent?: boolean | undefined
  /** Explicit sidebar mailbox picks (visibility keys). */
  mailboxKeysExplicit: string[]
  /** Mailboxes hidden via the footer toggles (count only). */
  hiddenMailboxCount: number
  /** Categories muted via right-click (count only). */
  mutedCategoryCount: number
  /** Operator tokens parsed out of the (debounced) search input. */
  searchTokens: ParsedOperatorToken[]
}

/** Trim an ISO datetime to its calendar-day portion for display. */
function isoDay(value: string): string {
  return value.slice(0, 10)
}

/**
 * f(pills) → how many of them actually narrow the query.
 *
 * The funnel badge and the pill strip must agree, so both derive from this
 * one list rather than re-deriving "is something filtering?" from raw
 * state. Standing preferences are excluded on purpose: they are on by
 * default, and a badge that is never zero tells the operator nothing.
 */
export function countNarrowingPills(pills: readonly FilterPill[]): number {
  return pills.reduce(
    (total, pill) => total + (pill.kind === 'filter' ? 1 : 0),
    0,
  )
}

/**
 * f(filter snapshot) → ordered pill list.
 *
 * Ordering is deliberate: the dimensions the operator most recently
 * *acts* on (categories, read/star) lead; standing preferences
 * (mailboxes hidden, mutes) trail so they read as context, not action.
 */
export function buildInboxFilterPills(s: InboxFilterPillState): FilterPill[] {
  const pills: FilterPill[] = []

  for (const cat of s.selectedCategories) {
    pills.push({
      id: `cat:${cat}`,
      label: s.categoryLabel(cat),
      title: `Remove the ${s.categoryLabel(cat)} category filter`,
      kind: 'filter',
    })
  }
  if (s.isReadFilter !== undefined) {
    pills.push({
      id: 'read',
      label: s.isReadFilter ? 'Read' : 'Unread',
      title: 'Remove the read-state filter',
      kind: 'filter',
    })
  }
  if (s.isStarredFilter !== undefined) {
    pills.push({
      id: 'starred',
      label: s.isStarredFilter ? 'Starred' : 'Not starred',
      title: 'Remove the starred filter',
      kind: 'filter',
    })
  }
  if (s.allocationFilter) {
    pills.push({
      id: 'allocation',
      label: s.allocationFilter === 'allocated' ? 'Allocated' : 'Unallocated',
      title: 'Remove the board-allocation filter',
      kind: 'filter',
    })
  }
  if (s.jobIdFilter) {
    pills.push({
      id: 'job',
      label: `Job #${s.jobIdFilter}`,
      title: 'Remove the linked-job filter',
      kind: 'filter',
    })
  }
  if (s.jobIdsIn.length > 0) {
    const shown = s.jobIdsIn.slice(0, 3).map((j) => `#${j}`).join(', ')
    const extra = s.jobIdsIn.length - 3
    pills.push({
      id: 'jobs-in',
      label: `Jobs: ${shown}${extra > 0 ? ` +${extra}` : ''}`,
      title: 'Remove the multi-job filter',
      kind: 'filter',
    })
  }
  if (s.fromAddressContains) {
    pills.push({
      id: 'from',
      label: `From: ${s.fromAddressContains}`,
      title: 'Remove the sender filter',
      kind: 'filter',
    })
  }
  if (s.companyDomain) {
    pills.push({
      id: 'company',
      label: `Company: ${s.companyDomain}`,
      title: 'Remove the company-domain filter',
      kind: 'filter',
    })
  }
  if (s.receivedAtGte || s.receivedAtLte) {
    const from = s.receivedAtGte ? isoDay(s.receivedAtGte) : undefined
    const to = s.receivedAtLte ? isoDay(s.receivedAtLte) : undefined
    pills.push({
      id: 'date',
      label:
        from && to ? `${from} → ${to}` : from ? `After ${from}` : `Before ${to}`,
      title: 'Remove the date-range filter',
      kind: 'filter',
    })
  }
  if (s.hasAttachments !== undefined) {
    pills.push({
      id: 'attachments',
      label: s.hasAttachments ? 'Has attachment' : 'No attachment',
      title: 'Remove the attachment filter',
      kind: 'filter',
    })
  }
  if (s.hasCalendarEvent !== undefined) {
    pills.push({
      id: 'calendar',
      label: s.hasCalendarEvent ? 'Has invite' : 'No invite',
      title: 'Remove the calendar-invite filter',
      kind: 'filter',
    })
  }
  if (s.mailboxKeysExplicit.length > 0) {
    pills.push({
      id: 'mailboxes',
      label: `Mailboxes: ${s.mailboxKeysExplicit.length} selected`,
      title: 'Show all mailboxes (clears the sidebar mailbox picks)',
      kind: 'filter',
    })
  }
  // Search-operator tokens (from:mark, has:attachment, …). These narrow
  // the query exactly like sidebar fields but used to be invisible once
  // typed. Removing a token pill strips it from the search text.
  for (const token of s.searchTokens) {
    pills.push({
      id: `token:${token.key}`,
      label: `${token.key}: ${token.rawValue}`,
      title: `Remove the ${token.key}: token from the search`,
      kind: 'filter',
    })
  }
  // Standing preferences — legibility context, not counted as filters.
  if (s.hiddenMailboxCount > 0) {
    pills.push({
      id: 'mailboxes-hidden',
      label: `${s.hiddenMailboxCount} mailbox${s.hiddenMailboxCount === 1 ? '' : 'es'} hidden`,
      title: 'Show every connected mailbox (footer toggles)',
      kind: 'standing',
    })
  }
  if (s.mutedCategoryCount > 0) {
    pills.push({
      id: 'muted',
      label: `${s.mutedCategoryCount} muted`,
      title: 'Unmute all muted categories',
      kind: 'standing',
    })
  }

  return pills
}

/**
 * Strip every occurrence of an operator token (e.g. all `from:` tokens)
 * from a raw search input, preserving free text and other tokens.
 * Pure I→O — the token-pill × handler feeds the result back into
 * setSearchQuery.
 */
export function removeSearchToken(
  input: string,
  key: EmailSearchOperatorKey,
): string {
  const { tokens } = tokenizeEmailSearch(input)
  const kept = tokens.filter((t) => {
    const colonIdx = t.indexOf(':')
    if (colonIdx <= 0 || colonIdx === t.length - 1) return true
    return t.slice(0, colonIdx).toLowerCase() !== key
  })
  return kept.join(' ')
}
